import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getToken, setToken, getCartId } from "@/api";
import { toast } from "sonner";

const StoreContext = createContext(null);

export const useStore = () => useContext(StoreContext);

export const formatINR = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export const formatDateRange = (est) => {
  if (!est?.min_date) return "";
  const fmt = (d) => { const dt = new Date(d); return `${dt.getDate()} ${dt.toLocaleString("en-IN", { month: "short" })}`; };
  const a = fmt(est.min_date);
  const b = fmt(est.max_date);
  return a === b ? a : `${a} – ${b}`;
};

export function StoreProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [menus, setMenus] = useState([]);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [cart, setCart] = useState({ items: [], totals: null });
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlistIds, setWishlistIds] = useState([]);

  const refreshCart = useCallback(async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (e) { /* silent */ }
  }, []);

  const refreshWishlist = useCallback(async () => {
    if (!getToken()) { setWishlistIds([]); return; }
    try {
      const { data } = await api.get("/auth/wishlist");
      setWishlistIds(data.map((p) => p.id));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    api.get("/store/bootstrap").then(({ data }) => {
      setSettings(data.settings);
      setMenus(data.menus);
    }).catch(() => {});
    refreshCart();
    if (getToken()) {
      api.get("/auth/me").then(({ data }) => { setUser(data); setAuthChecked(true); refreshWishlist(); })
        .catch(() => { setToken(null); setAuthChecked(true); });
    } else setAuthChecked(true);
  }, [refreshCart, refreshWishlist]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    await api.post("/cart/merge", { cart_id: getCartId() }).catch(() => {});
    await refreshCart();
    await refreshWishlist();
    return data.user;
  };

  const register = async (name, email, password, phone) => {
    const { data } = await api.post("/auth/register", { name, email, password, phone });
    setToken(data.token);
    setUser(data.user);
    await api.post("/cart/merge", { cart_id: getCartId() }).catch(() => {});
    await refreshCart();
    return data.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setWishlistIds([]);
    toast.success("Logged out");
  };

  const addToCart = async (product_id, variant_id, qty = 1) => {
    const { data } = await api.post("/cart/items", { product_id, variant_id, qty });
    setCart(data);
    setCartOpen(true);
  };

  const updateCartItem = async (variant_id, qty) => {
    const { data } = await api.put(`/cart/items/${variant_id}`, { qty });
    setCart(data);
  };

  const removeCartItem = async (variant_id) => {
    const { data } = await api.delete(`/cart/items/${variant_id}`);
    setCart(data);
  };

  const toggleWishlist = async (productId) => {
    if (!user) { toast.info("Please login to save to your wishlist"); return false; }
    if (wishlistIds.includes(productId)) {
      await api.delete(`/auth/wishlist/${productId}`);
      setWishlistIds((ids) => ids.filter((i) => i !== productId));
      toast.success("Removed from wishlist");
    } else {
      await api.post(`/auth/wishlist/${productId}`);
      setWishlistIds((ids) => [...ids, productId]);
      toast.success("Saved to wishlist");
    }
    return true;
  };

  const cartCount = (cart.items || []).reduce((s, i) => s + i.qty, 0);

  return (
    <StoreContext.Provider value={{
      settings, menus, user, authChecked, cart, cartCount, cartOpen, setCartOpen,
      wishlistIds, login, register, logout, addToCart, updateCartItem, removeCartItem,
      refreshCart, toggleWishlist, refreshWishlist,
    }}>
      {children}
    </StoreContext.Provider>
  );
}
